import { Button } from '@/components/ui/button'
import { cardTemplates, getPlanTemplates } from '@/config/cardTemplates'
import { navigatetoHome } from '@/utils/footer-navigations'
import { Lock, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { generateCardHTML } from '../../config/cardGenerator'

const TemplateSelector = ({ plan, onSelect }) => {
  const navigate = useNavigate()
  const availableTemplates = getPlanTemplates(plan)

  const TemplateLockOverlay = ({ template }) => (
    <div className="rounded-lg absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <div className="flex items-center gap-2">
        <Lock className="w-5 h-5 text-white" />
        <span className="text-sm text-white font-medium">
          {template.isBlank
            ? 'Enterprise Feature'
            : `${template.planLevel.charAt(0).toUpperCase()}${template.planLevel.slice(1)} Template`}
        </span>
      </div>
      <Button
        className="bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-3 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          navigatetoHome('/#prices')
        }}
      >
        Upgrade Plan
      </Button>
    </div>
  )

  return (
    <div className="container mx-auto p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-theme-color-700 to-blue-600 text-transparent bg-clip-text">
            Choose Your Template
          </h2>
          <p className="text-gray-600 mt-2">
            Select a pre-designed template or create your own masterpiece
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div className="flex flex-wrap gap-6 justify-center">
        {cardTemplates.map((template, index) => {
          const isAvailable = availableTemplates.includes(template)
          const isBlank = template.isBlank

          return (
            <div
              key={index}
              onClick={() => isAvailable && onSelect(template)}
              className="relative group cursor-pointer"
            >
              <div
                className={`
                w-[490px] h-[248px] rounded-lg overflow-hidden transition-all duration-300
                ${isAvailable ? 'hover:shadow-xl hover:scale-[1.02]' : 'opacity-90'}
                ${isBlank ? 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-dashed border-blue-200' : 'border'}
              `}
              >
                {isBlank ? (
                  // Custom Template Content
                  <div className="h-full p-8 flex flex-col items-center justify-center text-center relative">
                    {/* Animated background effects */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent)] animate-pulse" />
                    <div className="absolute inset-0 bg-[conic-gradient(from_90deg_at_50%_50%,rgba(79,70,229,0.1),transparent)] animate-spin-slow" />

                    {/* Content */}
                    <div className="relative z-10">
                      <Sparkles className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-pulse" />
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text mb-3">
                        Create Custom Design
                      </h3>
                      <p className="text-gray-600 mb-6 max-w-sm">
                        Move anything, turn ideas into reality, and make it yours.
                      </p>
                    </div>
                  </div>
                ) : (
                  // Regular Template Preview
                  <div
                    style={{ transform: 'scale(0.7)', transformOrigin: '0 0' }}
                    dangerouslySetInnerHTML={{ __html: generateCardHTML(template?.formData, true) }}
                  />
                )}
              </div>

              {/* Lock Overlay - shown for unavailable templates */}
              {!isAvailable && <TemplateLockOverlay template={template} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TemplateSelector
